import { GlobalAlert } from '@thoughtspot/global-alert';
import { useTranslation } from '@thoughtspot/i18n';
import { jsonConstants } from '@thoughtspot/json-constants';
import { Alert, BannerAlertType, } from '@thoughtspot/radiant-react/widgets/alert';
import { Button } from '@thoughtspot/radiant-react/widgets/button';
import { IconID } from '@thoughtspot/radiant-react/widgets/icon';
import { LoadingIndicator } from '@thoughtspot/radiant-react/widgets/loading-indicator';
import { Typography } from '@thoughtspot/radiant-react/widgets/typography';
import { aiModelType, enableOnboardingConfigPerOrg, getBooleanSessionFlag, getCurrentOrgId, getSessionInfo, isCustomerLLMAccount, isThirdPartyAnalysisEnabled, setSessionInfo, } from '@thoughtspot/session-service';
import _ from 'lodash';
import React, { Suspense, useCallback, useEffect, useMemo, useState, } from 'react';
import { BasePageContext } from "../../contexts/basepage-context";
import { useDisableSageOnAllSourcesMutation } from "../../services/generated/graphql-types";
import { useGetLLMGatewayConfigQuery } from "../../services/llm-gateway/llm-gateway-service";
import { useGetFeatureLazyQuery } from "../../services/tscli/tscli-service";
import { MLIntegrationsContainerAdmin } from '../spotiq/ml-integration/mlIntegration.container';
import { ConfigPrePostFixStrings, EA_FEATURE_TAB, TranslationsKeys, } from './common';
import { EditSettings } from './editscreen/editscreen.container';
import { getIsLLMGatewayEnabled, isLLMVisibilityEnabled, } from './llm-gateway/llm-gateway.util';
import { Setting } from './setting/index';
import { SettingHeader } from './setting/setting-header';
import { SettingSeperator } from './setting-seperator';
import { SettingSpotterConnectors } from './setting-spotter-connectors.container';
import { getViewMode, isConfigurationVisible, isSettingVisible, } from './settings.container.util';
import styles from './settings.module.scss';
import { ThoughtspotSageLinks } from './settings-sage-links';
const EMPTY_VALUE_REPLACEMENT_STRING = '--';
const AI_MODEL_TYPE = 'aiModelType';
const CONNECTOR_EDIT_TYPE = 'connectorEdit';
export const Settings = ({ id, currentOrgId, }) => {
    const { t } = useTranslation();
    const { connectorsComponents, sessionService } = BasePageContext();
    const [settingsData, setSettingsData] = useState(null);
    const [isRefetching, setIsRefetching] = useState(false);
    const [currentViewState, setViewState] = useState('view');
    const [selectedSetting, setSelectedSetting] = useState({});
    const [isBannerVisible, setIsBannerVisible] = React.useState(false);
    const [isToastVisible, setIsToastVisible] = React.useState(false);
    const [statusMessage, setStatusMessage] = useState('');
    const [toastMessage, setToastMessage] = useState('');
    const [bannerType, setBannerType] = useState(BannerAlertType.ERROR);
    const isCustomerLLMAcct = isCustomerLLMAccount();
    const providerType = aiModelType();
    const { getLlmGatewayConfig, data: llmConfigData, error: llmConfigError, loading: llmConfigLoading, } = useGetLLMGatewayConfigQuery();
    const [disableSageOnAllSourcesMutation,] = useDisableSageOnAllSourcesMutation();
    const viewMode = getViewMode(currentOrgId);
    const { data: settingsResponse, error, loading: isLoading, getFeature, } = useGetFeatureLazyQuery(id, viewMode);
    useEffect(() => {
        getFeature();
    }, [id]);
    const getTranslations = useMemo(() => (key) => t(`${key}`), [0]);
    const onboardingSignUpConfigKeys = [
        'orion.signUpConfiguration.signUpEnabled',
        'orion.signUpConfiguration.signUpButtonText',
        'orion.signUpConfiguration.signUpButtonLink',
    ];
    const onboardingFromEmailKey = 'orion.emailConfig.welcomeEmailConfig.fromEmail';
    const alertAcrossAttributesKey = 'orion.a3Config.alertAcrossAttributesEnabled';
    const updateOnboardingConfigFn = (data) => {
        if (!data?.settings?.[0]?.configs) {
            setSettingsData(data);
            return;
        }
        const updatedOnboardingConfig = data.settings[0].configs.map((config) => {
            if (getCurrentOrgId() !== 0 &&
                onboardingSignUpConfigKeys.includes(config.configName)) {
                return {
                    ...config,
                    disabled: true,
                };
            }
            if (config.configName === onboardingFromEmailKey) {
                return {
                    ...config,
                    disabled: true,
                };
            }
            return { ...config, disabled: false };
        });
        const updatedSettingsData = {
            ...data,
            settings: [
                {
                    ...data.settings[0],
                    configs: updatedOnboardingConfig,
                },
            ],
        };
        setSettingsData(updatedSettingsData);
    };
    const updateAlertAcrossAttributeSettings = (data) => {
        if (!data?.settings?.[0]?.configs) {
            setSettingsData(data);
            return;
        }
        const isJwtTokenFeatureEnabled = getBooleanSessionFlag(jsonConstants.JWT_TOKEN_FEATURE_ENABLED);
        const isMandatoryTokenFilterFeatureEnabled = getBooleanSessionFlag(jsonConstants.MANDATORY_TOKEN_FILTER_FEATURE_ENABLED);
        const shouldDisable = isJwtTokenFeatureEnabled && isMandatoryTokenFilterFeatureEnabled;
        const updatedAlertAcrossAttributeConfig = data.settings[0].configs.map((config) => {
            if (config.configName === alertAcrossAttributesKey &&
                shouldDisable) {
                return { ...config, disabled: true };
            }
            return config;
        });
        const updatedSettingsData = {
            ...data,
            settings: [
                {
                    ...data.settings[0],
                    configs: updatedAlertAcrossAttributeConfig,
                },
            ],
        };
        setSettingsData(updatedSettingsData);
    };
    const fetchSettings = () => {
        setViewState('view');
        if (settingsResponse && !isLoading && settingsResponse.Data) {
            setIsRefetching(false);
            if (isBannerVisible) {
                setIsBannerVisible(false);
            }
            if (id === 'onboarding' && enableOnboardingConfigPerOrg()) {
                updateOnboardingConfigFn(settingsResponse.Data);
            }
            else if (id === 'early_access_feature') {
                updateAlertAcrossAttributeSettings(settingsResponse.Data);
            }
            else {
                let settingResponseData = settingsResponse.Data;
                if (id === 'sage_feature') {
                    const nonSageExperienceSettings = settingsResponse.Data.settings?.filter(setting => setting.settingName !==
                        'spotterActivationTargets') || [];
                    const sageExperienceSettings = settingsResponse.Data.settings?.filter(setting => setting.settingName === 'spotterActivationTargets')?.[0];
                    if (sageExperienceSettings) {
                        const sageExperienceSettingsIndex = settingsResponse.Data.settings.indexOf(sageExperienceSettings);
                        const sageExperienceSettingsConfigsWithoutEureka = sageExperienceSettings.configs;
                        const sageExperieceSettingsWithoutEurekaConfig = {
                            ...sageExperienceSettings,
                            configs: sageExperienceSettingsConfigsWithoutEureka,
                        };
                        settingResponseData = {
                            ...settingsResponse.Data,
                            settings: [...nonSageExperienceSettings],
                        };
                        settingResponseData.settings.splice(sageExperienceSettingsIndex, 0, sageExperieceSettingsWithoutEurekaConfig);
                    }
                    else {
                        settingResponseData = {
                            ...settingsResponse.Data,
                            settings: [...nonSageExperienceSettings],
                        };
                    }
                }
                setSettingsData(settingResponseData);
            }
        }
        else if ((settingsResponse &&
            !isLoading &&
            (!settingsResponse.Data || settingsResponse.Error)) ||
            error) {
            setIsRefetching(false);
            if (settingsData) {
                setSettingsData(null);
            }
            setStatusMessage(getTranslations(TranslationsKeys.SETTINGS_GET_FAILURE_MESSAGE));
            setBannerType(BannerAlertType.ERROR);
            setIsBannerVisible(true);
        }
    };
    useEffect(() => {
        fetchSettings();
    }, [id, settingsResponse]);
    const llmGatewayEnabled = getIsLLMGatewayEnabled();
    useEffect(() => {
        if (llmGatewayEnabled &&
            id === 'sage_feature' &&
            currentViewState === 'view') {
            getLlmGatewayConfig();
        }
    }, [getLlmGatewayConfig, llmGatewayEnabled, id, currentViewState]);
    const actionHandler = useCallback((type, selectedSettingConfig) => {
        setIsBannerVisible(false);
        setIsToastVisible(false);
        setViewState('edit');
        setSelectedSetting(selectedSettingConfig);
    }, []);
    const spotterConnectorsActionHandler = useCallback((type) => actionHandler(type, { editType: CONNECTOR_EDIT_TYPE }), [actionHandler]);
    const infoTooltipIcon = useCallback((configName, label) => (React.createElement(Typography, { variant: "p" },
        label,
        ' ',
        React.createElement(Button, { tooltip: t(`${configName}.${ConfigPrePostFixStrings.INFO_TOOLTIP}`), ariaLabel: t(`${configName}.${ConfigPrePostFixStrings.INFO_TOOLTIP}`), type: "secondary", icon: IconID.INFORMATION, size: "s", backgroundType: "light", className: styles.infoBtnTransparent }))), [t]);
    const parseSettingsConfig = useCallback((setting) => {
        const filterConfigsToShow = setting.configs?.filter((inputConfig) => isConfigurationVisible(inputConfig.configName)) || [];
        let configsToProcess = filterConfigsToShow;
        if (setting.settingName === 'querySetting') {
            const allConfigs = setting.configs || [];
            const countWithNullConfig = allConfigs.find((config) => config.configName === 'orion.countWithNull');
            const countWithNullGflag = allConfigs.find((config) => config.configName === 'ac_count_with_null');
            const uniqueCountWithNullGflag = allConfigs.find((config) => config.configName === 'unique_count_with_null');
            const allCountNullEnabled = countWithNullConfig?.value === 'true' &&
                countWithNullGflag?.value === 'true' &&
                uniqueCountWithNullGflag?.value === 'true';
            const virtualCountNullToggle = {
                configName: 'enableCountWithNull_Virtual',
                elementType: 'toggle',
                value: allCountNullEnabled ? 'true' : 'false',
                infoTooltip: false,
                controlsFlags: [
                    'orion.countWithNull',
                    'ac_count_with_null',
                    'unique_count_with_null',
                ],
            };
            configsToProcess = [
                ...filterConfigsToShow,
                virtualCountNullToggle,
            ];
        }
        const isViewButtonEnabled = isLLMVisibilityEnabled();
        const tmpConfigs = configsToProcess.map((inputConfig) => {
            const { configName, value, elementType, elementConfig, validation, infoTooltip, orgAware, } = inputConfig;
            const settingID = configName;
            const label = `${t(`${configName}.${ConfigPrePostFixStrings.LABEL}`)}`;
            let displayValue = value;
            if (configName === 'ac_default_bucket_for_date') {
                const defaultLabel = t('ac_default_bucket_for_date.default.label');
                if (value === -1 ||
                    value === '-1' ||
                    value === 'Detailed' ||
                    value === defaultLabel ||
                    value === 'default') {
                    displayValue = 'default';
                }
            }
            const config = {
                label: infoTooltip
                    ? infoTooltipIcon(configName, label)
                    : label,
            };
            config.labelText = `${t(`${configName}.${ConfigPrePostFixStrings.LABEL}`, { lng: 'en-US' })}`;
            if (elementType === 'input' && !!elementConfig) {
                config.type = elementConfig.type;
            }
            if (elementType === 'select') {
                const optionsResult = elementConfig?.groups?.map((elementOptions) => {
                    const options = elementOptions?.options?.map((optionId) => ({
                        id: optionId,
                        label: t(`${configName}.${optionId}.${ConfigPrePostFixStrings.LABEL}`),
                    }));
                    return {
                        options,
                    };
                });
                config.options = optionsResult;
            }
            const configObject = {
                id: settingID,
                elementType,
                config,
                value: displayValue === ''
                    ? EMPTY_VALUE_REPLACEMENT_STRING
                    : displayValue,
                orgAware: Boolean(orgAware),
            };
            if (elementType !== 'toggle' && validation) {
                const tempValidation = {};
                Object.keys(validation).map(key => {
                    if (validation[key] && key !== '__typename') {
                        let validValue = validation[key];
                        if (validation[key] === 'true' ||
                            validation[key] === 'false') {
                            validValue = validation[key] === 'true';
                        }
                        tempValidation[key] = validValue;
                        tempValidation[`${key}Error`] = t(`${configName}.${key}Error`);
                    }
                });
                configObject.validation = tempValidation;
            }
            if (elementType === 'toggle') {
                if (configName === 'orion.enableSpotterAgentExperience') {
                    configObject.valueLabels = {
                        true: getTranslations(TranslationsKeys.SPOTTER_AGENT_LABEL),
                        false: getTranslations(TranslationsKeys.SPOTTER_CLASSIC_LABEL),
                    };
                }
                else if (configName === 'orion.isSpotterExperienceEnabled') {
                    configObject.valueLabels = {
                        true: getTranslations(TranslationsKeys.SPOTTER_LABEL),
                        false: getTranslations(TranslationsKeys.ASK_SAGE_LABEL),
                    };
                }
                else if (configName ===
                    'orion.arePdfCoverFilterPageDefaultsChecked' ||
                    configName === 'orion.exportConfig.removeCSVHeader') {
                    configObject.valueLabels = {
                        true: getTranslations(TranslationsKeys.YES_LABEL),
                        false: getTranslations(TranslationsKeys.NO_LABEL),
                    };
                }
                else {
                    configObject.valueLabels = {
                        true: getTranslations(TranslationsKeys.ENABLED_LABEL),
                        false: getTranslations(TranslationsKeys.DISABLED_LABEL),
                    };
                }
            }
            return configObject;
        });
        const actions = setting.actions
            .filter((action) => {
            if (!isViewButtonEnabled &&
                llmGatewayEnabled &&
                setting.settingName === AI_MODEL_TYPE &&
                action.type?.toLowerCase?.() === 'edit') {
                return false;
            }
            return true;
        })
            .map((action) => {
            const isAiModelTypeViewOnlyAction = llmGatewayEnabled &&
                setting.settingName === AI_MODEL_TYPE &&
                action.type?.toLowerCase?.() === 'edit';
            const tmpAction = {
                type: action.type,
                title: isAiModelTypeViewOnlyAction
                    ? t('tscliui.view.label')
                    : t(`tscliui.${action.type.toLowerCase()}.${ConfigPrePostFixStrings.LABEL}`),
            };
            return tmpAction;
        });
        const tmpSetting = {
            settingName: t(`${setting.settingName}.${ConfigPrePostFixStrings.TITLE}`),
            settingNameKey: setting.settingName,
            settingSubTitle: t(`${setting.settingName}.${ConfigPrePostFixStrings.SUBTITLE}`),
            resetToDefault: `${setting.resetToDefault}`,
            actions,
            configs: tmpConfigs,
        };
        if (setting.learnMore) {
            tmpSetting.learnMore = {
                ...setting.learnMore,
                text: id === EA_FEATURE_TAB
                    ? getTranslations(TranslationsKeys.CLICK_HERE)
                    : getTranslations(TranslationsKeys.LEARN_MORE),
            };
        }
        return tmpSetting;
    }, [t, infoTooltipIcon, llmGatewayEnabled, id]);
    const backHandler = useCallback(({ isSuccess, reload, type, data }) => {
        setViewState('view');
        setSelectedSetting({});
        if (type === 'BACK' && !data) {
            return;
        }
        if (type === 'BACK' && data) {
            const existingSessionInfo = getSessionInfo();
            const newSessionInfo = {
                ...existingSessionInfo,
                configInfo: {
                    ...existingSessionInfo.configInfo,
                    isCustomerLLMAccount: data.isCustomerLLMAccount,
                    aiModelType: data.provider,
                },
            };
            setSessionInfo(newSessionInfo);
            setToastMessage(getTranslations(TranslationsKeys.LLM_SUCCESS_MESSAGE));
            setIsToastVisible(true);
            return;
        }
        if (type === 'UPDATE' && reload && isSuccess) {
            setToastMessage(getTranslations(TranslationsKeys.APP_SETTINGS_SAVE_SUCCESS_MESSAGE));
            setIsToastVisible(true);
            if (id === 'onboarding' && enableOnboardingConfigPerOrg()) {
                updateOnboardingConfigFn(data);
            }
            else {
                setIsRefetching(true);
                getFeature();
            }
        }
        else if (type === 'UPDATE' && !isSuccess && !reload) {
            setStatusMessage(getTranslations(TranslationsKeys.FAILURE_MESSAGE));
            setBannerType(BannerAlertType.ERROR);
            setIsBannerVisible(true);
        }
    }, [id]);
    const primaryActionForToast = {
        label: t('admin.refresh'),
        onClick: () => {
            window.location.reload();
        },
    };
    const sectionAlertBanner = useMemo(() => (React.createElement("div", { className: styles.inlineAlertBanner },
        React.createElement(Alert.Banner, { isVisible: true, message: id === EA_FEATURE_TAB
                ? ''
                : getTranslations(TranslationsKeys.SETTINGS_INFO_MESSAGE), showCloseButton: false, primaryAction: id === EA_FEATURE_TAB
                ? undefined
                : primaryActionForToast, type: BannerAlertType.INFO }))), [id]);
    const showEmptyMessages = {
        early_access_feature: (React.createElement("span", null, t(TranslationsKeys.EARLY_ACCESS_EMPTY_SCREEN_BANNER_MESSAGE))),
    };
    const showEmptyScreenBanner = useMemo(() => (React.createElement("div", { className: styles.inlineAlertBanner },
        React.createElement(Alert.Banner, { isVisible: true, message: showEmptyMessages[settingsData?.groupName], showCloseButton: false, type: BannerAlertType.INFO }))), [settingsData?.groupName]);
    const renderEditSettingScreen = useMemo(() => {
        const { configs, learnMore, settingName, settingNameKey, settingSubTitle, resetToDefault, editType, } = selectedSetting;
        if (editType === CONNECTOR_EDIT_TYPE) {
            const { AdminConnectorsContainer } = connectorsComponents || {};
            return AdminConnectorsContainer ? (React.createElement(Suspense, { fallback: React.createElement(LoadingIndicator, null,
                    React.createElement(React.Fragment, null)) },
                React.createElement(AdminConnectorsContainer, { actionHandler: backHandler }))) : null;
        }
        let tempConfigs;
        let updatedTempConfigs;
        const configsMap = new Map();
        if (configs && settingsData) {
            tempConfigs = Object.assign(configs);
            updatedTempConfigs = tempConfigs.map((config) => ({
                ...config,
                value: config.value === EMPTY_VALUE_REPLACEMENT_STRING
                    ? ''
                    : config.value,
                config: {
                    ...config.config,
                    disabled: settingsData?.settings?.[0]?.configs?.find((item) => item.configName === config.id)?.disabled || false,
                },
            }));
            updatedTempConfigs.forEach((tempConfig) => {
                configsMap.set(tempConfig.id, _.cloneDeep(tempConfig));
            });
        }
        return (settingsData && (React.createElement(EditSettings, { settingId: settingsData.groupName, pageTitle: t(`${ConfigPrePostFixStrings.TSCLI_CONFIG}.${settingsData.groupName}.${ConfigPrePostFixStrings.TITLE}`), title: settingName, titleKey: settingNameKey, subTitle: settingSubTitle, configs: updatedTempConfigs, configsMap: configsMap, learnMore: learnMore, backHandler: (data) => backHandler(data), resetToDefault: resetToDefault, isCustomerLLMAcct: isCustomerLLMAcct, providerType: providerType, viewMode: viewMode, allPageSettings: settingsData.settings })));
    }, [selectedSetting]);
    const renderSetting = (setting) => {
        const formatProviderName = (providerStr) => {
            return providerStr
                ?.replace(/_/g, ' ')
                .split(' ')
                .map(word => word.charAt(0).toUpperCase() +
                word.slice(1).toLowerCase())
                .join(' ');
        };
        const getProvider = () => {
            if (setting.settingName === AI_MODEL_TYPE && llmGatewayEnabled) {
                if (llmConfigLoading) {
                    return t('loading');
                }
                const { mode, selection, manualProvider } = llmConfigData?.data || {};
                if (llmConfigData?.error || llmConfigError) {
                    return '-';
                }
                if (mode === 'byom') {
                    return t('orion.aiModelType.LLMGatewayv2.custom');
                }
                if (selection === 'manual') {
                    return `${t('orion.aiModelType.LLMGatewayv2.tsEnabled')} (${formatProviderName(manualProvider)})`;
                }
                return t('orion.aiModelType.LLMGatewayv2.default');
            }
            return providerType;
        };
        return (React.createElement("div", { key: setting.settingName },
            id !== EA_FEATURE_TAB && React.createElement(SettingSeperator, null),
            React.createElement(Setting, { actionHandler: actionHandler, setting: parseSettingsConfig(setting), isCustomerLLMAccount: isCustomerLLMAcct, provider: getProvider() }),
            setting.settingName === AI_MODEL_TYPE &&
                id === 'sage_feature' && (React.createElement(React.Fragment, null, sessionService.isMCPAdminConnectorsEnabled() && (React.createElement(SettingSpotterConnectors, { actionHandler: spotterConnectorsActionHandler })))),
            setting.settingName === AI_MODEL_TYPE && (React.createElement(ThoughtspotSageLinks, { onClickDisableWorksheets: async () => {
                    try {
                        await disableSageOnAllSourcesMutation();
                        setIsToastVisible(true);
                        setToastMessage(t('a3.preferences.toastSuccess'));
                    }
                    catch (err) {
                        setIsToastVisible(true);
                        setToastMessage(t('a3.preferences.genericError'));
                    }
                } }))));
    };
    return (React.createElement(React.Fragment, null,
        React.createElement(GlobalAlert.Banner, { isVisible: isBannerVisible, message: statusMessage, type: bannerType, onClose: () => setIsBannerVisible(false) }),
        React.createElement(GlobalAlert.Toast, { isVisible: isToastVisible, message: toastMessage, primaryAction: primaryActionForToast, onClose: () => setIsToastVisible(false), autoHide: true }),
        (isLoading || isRefetching) && (React.createElement(LoadingIndicator.Global, { hideLoader: false, className: styles.adminSettingsFullScreenLoader },
            React.createElement(React.Fragment, null))),
        React.createElement("div", { className: styles.advanceSettings },
            !(isLoading && !isRefetching) &&
                currentViewState === 'view' &&
                settingsData &&
                settingsData.groupName !== EA_FEATURE_TAB && (React.createElement(SettingHeader, { titleSize: "large", settingName: t(`${ConfigPrePostFixStrings.TSCLI_CONFIG}.${settingsData.groupName}.${ConfigPrePostFixStrings.TITLE}`), settingSubTitle: t(`${ConfigPrePostFixStrings.TSCLI_CONFIG}.${settingsData.groupName}.${ConfigPrePostFixStrings.SUBTITLE}`), learnMore: settingsData.learnMore
                    ? {
                        ...settingsData.learnMore,
                        text: getTranslations(TranslationsKeys.LEARN_MORE),
                    }
                    : undefined })),
            React.createElement("div", { className: styles.settingsRow },
                settingsData &&
                    !(isLoading && !isRefetching) &&
                    currentViewState === 'view' &&
                    (settingsData.settings &&
                        (id !== 'onboarding' || !enableOnboardingConfigPerOrg())
                        ? sectionAlertBanner
                        : showEmptyScreenBanner),
                currentViewState === 'view'
                    ? !(isLoading && !isRefetching) &&
                        settingsData &&
                        settingsData.settings
                            ?.filter((setting) => isSettingVisible(setting.settingName))
                            .map((setting) => renderSetting(setting))
                    : renderEditSettingScreen),
            isThirdPartyAnalysisEnabled() &&
                !(isLoading && !isRefetching) &&
                currentViewState === 'view' &&
                settingsData &&
                settingsData.groupName === 'search' && (React.createElement("div", null,
                React.createElement(SettingSeperator, null),
                React.createElement(MLIntegrationsContainerAdmin, null))))));
};
//# sourceMappingURL=settings.container.js.map